import React, { useCallback, memo, useMemo } from 'react';
import { View } from 'react-native';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import Text from 'design-system/Text';
import Logo from 'design-system/Logo';
import Spacer from 'design-system/Spacer';
import { rem } from 'design-system/values';
import useColors from 'hooks/useColors';
import { useMainStackNavigation, useMainStackRoute } from 'utils/types/navigationV6';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import { useAppSelector } from 'store/hooks';
import FormattedAmount from './FormattedAmount';
import FormattedPriceChangePct from './FormattedPriceChangePct';
import StockAssetRow from './StockAssetRow';
import { useCurrentPrice, usePriceChange } from '../hooks/selectors';
import { ROW_ITEM_VERTICAL_PADDING, ROW_ITEM_HORIZONTAL_PADDING } from '../constants';
import useGetRealTimePrices from '../hooks/useGetRealTimePrices';
import { selectIsTradingAccountAlpaca } from '../reducers/selectors';
import { isWeb } from '../../../constants';
/** @deprecated Alpaca only */
const StockSearchResultAlpaca = React.memo(({ data, stockAlertFlow, recurringOrderFlow }) => {
    const styles = useStyles(styleSet);
    const { navigateDeprecated } = useMainStackNavigation();
    const currentPrice = useCurrentPrice(data.id);
    const currentPriceChange = usePriceChange(data.id);
    const colors = useColors();
    useGetRealTimePrices(data.id);
    const onPress = useCallback(() => {
        if (stockAlertFlow) {
            navigateDeprecated('InvestSetStockAlertScreen', { assetId: data.id });
            return;
        }
        navigateDeprecated('InvestStock', { assetId: data.id, recurringOrderFlow });
    }, [data, recurringOrderFlow, stockAlertFlow]);
    const borderStyles = useMemo(() => [data.isFirstItem && styles.firstItem, data.isLastItem && styles.lastItem], [data.isFirstItem, data.isLastItem, styles.firstItem, styles.lastItem]);
    return (<View style={[styles.wrapper, borderStyles]}>
      <TouchableHighlight hoverOnLight onCard={isWeb} onPress={onPress} activeOpacity={1} style={isWeb ? undefined : borderStyles} underlayColor={colors.background.underlay}>
        <View style={styles.container}>
          <Logo R44 uri={'iconUrl' in data ? data.iconUrl : data.icon} resizeMode="contain"/>
          <Spacer w={16}/>
          <View style={styles.row}>
            <View style={styles.flex}>
              <Text Text-16 numberOfLines={1}>
                {data.name}
              </Text>
              {!!data.symbol && <Text TextThin-14 Gray flex>{`$${data.symbol}`}</Text>}
            </View>
            {!recurringOrderFlow && (<>
                <Spacer w={16}/>
                <View style={styles.rightContent}>
                  <FormattedAmount amount={currentPrice}/>
                  <FormattedPriceChangePct priceChangePct={currentPriceChange?.percent}/>
                </View>
              </>)}
          </View>
        </View>
      </TouchableHighlight>
    </View>);
});
const StockSearchResult = ({ data, stockAlertFlow, recurringOrderFlow }) => {
    const isTradingAccountAlpaca = useAppSelector(selectIsTradingAccountAlpaca);
    const route = useMainStackRoute();
    const styles = useStyles(styleSet);
    const navigation = useMainStackNavigation();
    const colors = useColors();
    const onPress = useCallback(() => {
        if (route.params?.depositAndBuyFlowParams) {
            navigation.navigate('InvestStock', {
                assetId: data.id,
                depositAndBuyFlowParams: route.params?.depositAndBuyFlowParams,
            });
            return;
        }
        navigation.navigate('InvestStock', { assetId: data.id });
    }, [data.id, route.params?.depositAndBuyFlowParams]);
    const borderStyles = useMemo(() => [data.isFirstItem && styles.firstItem, data.isLastItem && styles.lastItem], [data.isFirstItem, data.isLastItem, styles.firstItem, styles.lastItem]);
    const viewData = useMemo(() => ({
        iconUrl: 'iconUrl' in data ? data.iconUrl : data.icon,
        name: data.name,
        description: 'description' in data ? data.description : undefined,
        shortDescription: data.shortDescription,
        averageAnnualReturn: 'averageAnnualReturn' in data ? data.averageAnnualReturn : undefined,
    }), [data]);
    if (isTradingAccountAlpaca) {
        return (<StockSearchResultAlpaca data={data} stockAlertFlow={stockAlertFlow} recurringOrderFlow={recurringOrderFlow}/>);
    }
    return (<View style={[styles.wrapper, borderStyles]}>
      <TouchableHighlight hoverOnLight onCard={isWeb} onPress={onPress} activeOpacity={1} style={isWeb ? undefined : borderStyles} underlayColor={colors.background.underlay}>
        <StockAssetRow {...viewData} style={styles.assetRow}/>
      </TouchableHighlight>
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    wrapper: {
        marginHorizontal: rem(16),
        backgroundColor: colors.cards.onDark,
        ...(isWeb && { paddingHorizontal: rem(8) }),
    },
    container: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: rem(ROW_ITEM_HORIZONTAL_PADDING),
        paddingVertical: rem(ROW_ITEM_VERTICAL_PADDING),
        // On web the card is padded by 8 and each row by 8, so the hover highlight sits inside the card
        ...(isWeb && { paddingHorizontal: rem(8) }),
    },
    // The web wrapper already pads 8, so the row only adds 8 to sit 16 in from the card
    assetRow: {
        ...(isWeb && { paddingHorizontal: rem(8) }),
    },
    rightContent: {
        alignItems: 'flex-end',
    },
    flex: {
        flex: 1,
    },
    row: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
    },
    firstItem: {
        borderTopLeftRadius: rem(16),
        borderTopRightRadius: rem(16),
        ...(isWeb && { paddingTop: rem(8) }),
    },
    lastItem: {
        borderBottomLeftRadius: rem(16),
        borderBottomRightRadius: rem(16),
        ...(isWeb && { paddingBottom: rem(8) }),
    },
}));
export default memo(StockSearchResult);
